import React, { useState } from 'react';
import { Organization } from '../../../../types/organization.types';
import { Input } from '@crisellevaldez/speculo-ui';
import { DataField } from './DataField';
import { SectionCard, FieldGrid, ItemCard, ItemGrid } from './SectionComponents';
import { Pencil, Check, X, Trash2 } from 'lucide-react';
import { useUpdateOnboardingData } from './useUpdateOnboardingData';
import { ConfirmationDialog } from '../../../common/dialogs/ConfirmationDialog';

interface IDXMLSSectionProps {
  selectedOrg: Organization;
  onboardingSetup: any;
  onboardingData: any;
  asTab?: boolean;
}

export const IDXMLSSection: React.FC<IDXMLSSectionProps> = ({ selectedOrg, onboardingData }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [localMlsEntries, setLocalMlsEntries] = useState<any[] | null>(null);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);

  const { updateMLSName, removeMLSEntry, saving } = useUpdateOnboardingData({
    organizationId: selectedOrg?.id,
  });

  const idxData = onboardingData.idx;
  // Use local state if available, otherwise use onboardingData
  const mlsEntries = localMlsEntries ?? idxData?.mls_entries ?? [];

  if (!idxData) {
    return <p className="text-gray-500 dark:text-gray-400 text-sm">No IDX/MLS data available</p>;
  }

  const siteData = idxData.site_data;
  const hasSubdomain = siteData?.idx_subdomain;
  const hasMlsEntries = idxData.mls_entries?.length > 0;
  const hasRegistrarInfo =
    siteData?.domain_registrar ||
    siteData?.registrar_username ||
    siteData?.registrar_email ||
    siteData?.registrar_phone;

  if (!hasSubdomain && !hasMlsEntries) {
    return <p className="text-gray-500 dark:text-gray-400 text-sm">No IDX/MLS data provided</p>;
  }

  const startEditing = (index: number, currentValue: string) => {
    setEditingIndex(index);
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleSave = async (index: number) => {
    const currentEntries = localMlsEntries ?? idxData?.mls_entries ?? [];
    const success = await updateMLSName(currentEntries, index, editValue, onboardingData);
    if (success) {
      // Update local state immediately so UI reflects the change
      const updatedEntries = [...currentEntries];
      updatedEntries[index] = { ...updatedEntries[index], mls_name: editValue };
      setLocalMlsEntries(updatedEntries);
      setEditingIndex(null);
      setEditValue('');
    }
  };

  const handleRemove = async (index: number) => {
    const currentEntries = localMlsEntries ?? idxData?.mls_entries ?? [];
    const success = await removeMLSEntry(currentEntries, index, onboardingData);
    if (success) {
      // Update local state immediately so UI reflects the removal
      setLocalMlsEntries(currentEntries.filter((_: any, i: number) => i !== index));
      // Clear any in-progress edit on the now-removed/shifted row
      setEditingIndex(null);
      setEditValue('');
    }
    setRemovingIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* IDX Domain Info */}
      {siteData && (
        <SectionCard title="IDX Domain Info">
          <FieldGrid>
            {siteData.idx_subdomain && (
              <DataField label="IDX Subdomain" value={siteData.idx_subdomain} />
            )}
            {siteData.website_domain && (
              <DataField label="Website Domain" value={siteData.website_domain} type="link" />
            )}
            {siteData.new_domain && (
              <DataField label="New Domain" value={siteData.new_domain} type="link" />
            )}
            {siteData.needs_domain_help !== undefined && (
              <DataField
                label="Needs Domain Help"
                value={siteData.needs_domain_help}
                type="boolean"
              />
            )}
          </FieldGrid>
        </SectionCard>
      )}

      {/* Registrar Info (from IDX "Need Help" flow) */}
      {hasRegistrarInfo && (
        <SectionCard title="Domain Registrar Info">
          <FieldGrid>
            {siteData.domain_registrar && (
              <DataField label="Domain Registrar" value={siteData.domain_registrar} />
            )}
            {siteData.registrar_email && (
              <DataField label="Account Email" value={siteData.registrar_email} type="email" />
            )}
            {siteData.registrar_phone && (
              <DataField label="Account Phone" value={siteData.registrar_phone} type="phone" />
            )}
          </FieldGrid>
          {(siteData.registrar_username || siteData.registrar_password) && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 italic">Login credentials sent via webhook</p>
          )}
        </SectionCard>
      )}

      {/* MLS Entries */}
      {mlsEntries.length > 0 && (
        <SectionCard title="MLS Entries">
          <ItemGrid>
            {mlsEntries.map((entry: any, index: number) => (
              <ItemCard key={index}>
                <div className="space-y-2">
                  {/* Editable MLS Name */}
                  {editingIndex === index ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Enter MLS name"
                          disabled={saving}
                        />
                      </div>
                      <button
                        onClick={() => handleSave(index)}
                        disabled={saving}
                        className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                        title="Save"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={cancelEditing}
                        disabled={saving}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 rounded"
                        title="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{entry.mls_name || '-'}</span>
                      <button
                        onClick={() => startEditing(index, entry.mls_name)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit MLS Name"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => setRemovingIndex(index)}
                        disabled={saving}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove MLS Entry"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Brokerage & Broker Info */}
                  <div className="text-sm space-y-1">
                    {entry.brokerage_name && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Brokerage:</span>{' '}
                        <span className="font-medium">{entry.brokerage_name}</span>
                      </div>
                    )}
                    {entry.broker_full_name && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Broker:</span>{' '}
                        <span className="font-medium">{entry.broker_full_name}</span>
                      </div>
                    )}
                    {entry.broker_email && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Broker Email:</span>{' '}
                        <span className="text-yellow-600">{entry.broker_email}</span>
                      </div>
                    )}
                  </div>

                  {/* Agent Info */}
                  {(entry.agent_name || entry.agent_mls_id || entry.agent_email) && (
                    <div className="text-sm space-y-1 pt-2 border-t border-gray-100 dark:border-white/10">
                      {entry.agent_name && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Agent:</span>{' '}
                          <span className="font-medium">{entry.agent_name}</span>
                        </div>
                      )}
                      {entry.agent_mls_id && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">MLS ID:</span>{' '}
                          <span className="font-medium">{entry.agent_mls_id}</span>
                        </div>
                      )}
                      {entry.agent_email && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Email:</span>{' '}
                          <span className="text-yellow-600">{entry.agent_email}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </ItemCard>
            ))}
          </ItemGrid>
        </SectionCard>
      )}

      <ConfirmationDialog
        isOpen={removingIndex !== null}
        onClose={() => setRemovingIndex(null)}
        onConfirm={() => removingIndex !== null && handleRemove(removingIndex)}
        title="Remove MLS Entry"
        message={
          removingIndex !== null
            ? `Remove "${mlsEntries[removingIndex]?.mls_name || 'this MLS entry'}" from the onboarding data? This only removes the MLS entry; the rest of the onboarding data is left unchanged.`
            : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={saving}
      />
    </div>
  );
};
